import { Box, Container, Typography } from "@mui/material";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { TestimonialCard } from "./TestimonialCard";
import { testimonials } from "../data/testimonials";
import { staggeredContainer } from "../utils/animations";
import { useState } from "react";

export const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const dragX = useMotionValue(0);
  const dragRotation = useTransform(dragX, [-200, 200], [45, -45]);

  const handleDragEnd = (event: any, info: any) => {
    const threshold = 50;

    if (Math.abs(info.offset.x) > threshold) {
      if (info.offset.x > 0 && activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      } else if (info.offset.x < 0 && activeIndex < testimonials.length - 1) {
        setActiveIndex(activeIndex + 1);
      }
    }

    animate(dragX, 0, { duration: 0.5 });
  };

  return (
    <Box
      sx={{
        py: 8,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <motion.div
          variants={staggeredContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <Typography
            variant="h2"
            gutterBottom
            align="left"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 600,
              mb: 6,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Endorsements
          </Typography>

          <Box
            sx={{
              perspective: "1000px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              position: "relative",
              userSelect: "none",
              mb: 6,
              overflow: "hidden",
            }}
          >
            <motion.div
              style={{
                x: dragX,
                rotateY: dragRotation,
                width: "100%",
                maxWidth: "650px",
              }}
              drag="x"
              dragConstraints={{ left: -100, right: 100 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: index === activeIndex ? "relative" : "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      visibility: index === activeIndex ? "visible" : "hidden",
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0.8, rotateY: 90, opacity: 0 }}
                      animate={{
                        scale: index === activeIndex ? 1 : 0.8,
                        rotateY: index === activeIndex ? 0 : 90,
                        opacity: index === activeIndex ? 1 : 0,
                      }}
                      transition={{ duration: 0.5 }}
                    >
                      <TestimonialCard {...testimonial} index={index} />
                    </motion.div>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>

          {/* Pagination dots */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mb: 2,
            }}
          >
            {testimonials.map((_, index) => (
              <Box
                key={index}
                onClick={() => setActiveIndex(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor:
                    index === activeIndex ? "secondary.main" : "grey.300",
                  transition: "background-color 0.3s",
                  "&:hover": {
                    backgroundColor:
                      index === activeIndex ? "secondary.main" : "grey.400",
                  },
                }}
              />
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};
